import React from 'react';
import { Box } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import {
  BodyText,
  Button,
  ModalBox,
  PortalOverlay,
  PortalOverlayHeader,
  PortalOverlayModal,
  HeaderText,
} from './ui';
import { ArrowsChevronLeftIcon, CloseIcon } from './ui/Icons';
import { PortalOverlayModalProps } from './ui/PortalOverlay/PortalOverlayModal';

interface CommonActionProps {
  action?: (modal: Modal) => void;
  text?: React.ReactNode;
  label?: string;
  testID?: string;
}

interface AsyncActionProps extends CommonActionProps {
  enabled: boolean;
  loading?: boolean;
}

interface ModalProps extends PortalOverlayModalProps {
  initialOpen?: boolean;
  title?: string;
  leftButton?: CommonActionProps;
  rightButton?: AsyncActionProps;
  hideHeader?: boolean;
  className?: string;
  onClose?: () => void;
  onOpen?: () => void;
  footerChildren?: React.ReactNode;
  zIndex?: number;
}

interface ModalState {
  open: boolean;
}

/**
 * @deprecated Use the PortalOverlayModal pattern instead
 */
class Modal extends React.PureComponent<ModalProps, ModalState> {
  /**  Button configs * */
  static CLOSE = {
    text: (<CloseIcon />),
    label: 'Close',
    action: modal => modal.close(),
    testID: 'modal-close',
  };

  static CLOSE_TEXT = {
    action: modal => modal.close(),
    text: (<BodyText color={BrandColor.Metal} weight="bold">Cancel</BodyText>),
    testID: 'modal-close',
  };

  static BACK = {
    label: 'Back',
    action: modal => modal.close(),
    testID: 'modal-back',
    text: (<ArrowsChevronLeftIcon />),
  };

  static SAVE = {
    text: (<BodyText color={BrandColor.FloraDark} weight="bold">Save</BodyText>),
    enabled: true,
    loading: false,
    action: modal => modal.close(),
    testID: 'modal-save',
  };

  /** Default properties * */
  static defaultProps = {
    leftButton: Modal.CLOSE,
    hideHeader: false,
  };

  state = {
    /** Modal is open and visible. */
    open: false,
  };

  handleOpen = () => {
    this.setState({ open: true });
    this.props.onOpen?.();
  };

  open = this.handleOpen;

  handleClose = async () => {
    if (!this.state.open) {
      return;
    }
    this.setState({ open: false });
    this.props.onClose?.();
  };

  // The close function is used in other components. This is an anti pattern, but
  // we need to support it until this deprecated modal is no longer used
  // eslint-disable-next-line react/no-unused-class-component-methods
  close = this.handleClose;

  componentDidMount() {
    if (this.props.initialOpen) {
      this.open();
    }
  }

  render() {
    const {
      children,
      hideHeader,
      title,
      leftButton,
      rightButton,
      footerChildren,
      onClose,
      onOpen,
      initialOpen,
      zIndex,
      ...restProps
    } = this.props;
    const { open } = this.state;

    if (!open) return null;
    const shouldShowHeader = !hideHeader && (
      !!title
      || !!leftButton
      || !!rightButton
    );

    return (
      <PortalOverlay
        name="DeprecateModal"
        onBackdropClick={this.handleClose}
        zIndex={zIndex}
      >
        <PortalOverlayModal
          position="fullScreen"
          {...restProps}
        >
          {!shouldShowHeader ? null : (
            <PortalOverlayHeader
              renderTitle={() => (
                <HeaderText
                  level="4"
                  textAlign="center"
                >
                  {title}
                </HeaderText>
              )}
              renderLeftAction={leftButton ? () => (
                <Box
                  paddingX={16}
                  paddingY={6}
                >
                  <Button
                    appearance="silent"
                    stopPropagation
                    onClick={() => leftButton.action?.(this)}
                    aria-label={leftButton.label}
                    testID={leftButton.testID || 'modal__left-button'}
                    style={{ lineHeight: 'unset', padding: 0 }}
                  >
                    {leftButton.text || null}
                  </Button>
                </Box>
              ) : null}
              renderRightAction={rightButton ? () => (
                <Box
                  paddingX={16}
                  paddingY={6}
                >
                  <Button
                    appearance="silent"
                    stopPropagation
                    onClick={() => rightButton.action?.(this)}
                    aria-label={rightButton.label}
                    testID={rightButton.testID || 'modal__right-button'}
                    style={{ lineHeight: 'unset', padding: 0 }}
                    disabled={!rightButton.enabled}
                  >
                    {rightButton.text || null}
                  </Button>
                </Box>
              ) : null}
            />
          )}
          <ModalBox scrollable padding={hideHeader ? 0 : undefined}>
            <Box>
              {children}
            </Box>
          </ModalBox>
          {footerChildren && (
            <Box padding={20} paddingTop={0}>
              {footerChildren}
            </Box>
          )}
        </PortalOverlayModal>
      </PortalOverlay>
    );
  }
}

export default Modal;
